import type { ResponseError } from "@lucaswickstrom/fetch";
import { useMutationWithAuthError } from "@ssf/auth-components";
import {
  type MutationOptions,
  type QueryOptions,
  queryClient,
  useQueryWithError,
} from "@ssf/components";
import {
  addComment,
  approveClub,
  type Club,
  type ClubCheck,
  ClubStatusLevel,
  createMember,
  createMembership,
  createSchema,
  getClub,
  getClubs,
  getClubTeams,
  getMember,
  getMemberTournaments,
  MembershipType,
  MemdbBoolean,
  OrgType,
  type Registration,
  RoleType,
  replaceMembership,
  SpecialType,
  type UnSchema,
  updateClubCheckValue,
  uploadDocuments,
} from "@ssf/memdb-api";
import type {
  MutationFunction,
  MutationKey,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

export { ClubStatusLevel };
export {
  type ClubNamePredictionProps,
  isClubNameAiGenerated,
  useClubNamePredictions,
} from "@ssf/memdb-components";

export function useQueryWrapper<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  queryOptions?: Partial<
    UseQueryOptions<TQueryFnData, ResponseError | Error, TData, TQueryKey>
  >,
) {
  return useQuery({ queryKey, queryFn, ...queryOptions });
}

export function useMutationWrapper<
  TData = unknown,
  TVariables = void,
  TMutationKey extends MutationKey = MutationKey,
  TContext = unknown,
>(
  mutationKey: TMutationKey,
  mutationFn: MutationFunction<TData, TVariables>,
  mutationOptions?: UseMutationOptions<
    TData,
    ResponseError | Error,
    TVariables,
    TContext
  >,
) {
  return useMutation({ mutationKey, mutationFn, ...mutationOptions });
}

const selectClubSchema = {
  id: 1,
  name: 1,
  schoolname: 1,
  active: 1,
  startdate: 1,
  specialType: 1,
  schoolclub: 1,
  noeconomy: 1,
  approvalStatus: [
    { referenceid: 1, approved: 1, signed: 1, documentid: 1, updated: 1 },
  ],
  registrations: [{ memberid: 1, documentid: 1, date: 1 }],
  clubStatus: [{ status: 1, year: 1 }],
  city: 1,
  district: { active: 1 },
  roles: [
    {
      orgrole: 1,
      end: 1,
      filter: `(end=1970-01-01)`,
      memberid: 1,
    },
  ],
};

const getSelectClubsWithSchema = (memberId: number) =>
  getMember(memberId, {
    name: 1,
    registrations: [
      {
        regend: 1,
        club: selectClubSchema,
      },
    ],
    roles: [
      {
        orgtype: 1,
        orgrole: 1,
        end: 1,
        club: selectClubSchema,
        filter: `(orgtype=${OrgType.Club})`,
      },
    ],
  }).then(
    (data) =>
      data.roles.reduce<
        Record<
          number,
          (typeof data)["registrations"][0]["club"] & { admin: boolean }
        >
      >(
        (internalClus, role) => ({
          // biome-ignore lint/performance/noAccumulatingSpread: ignore
          ...internalClus,
          [role.club.id]: {
            ...role.club,
            admin:
              (role.orgrole === RoleType.Admin && role.end === "1970-01-01") ||
              internalClus[role.club.id]?.admin,
          },
        }),
        Object.fromEntries(
          data.registrations
            .filter(({ club }) => club.district.active === MemdbBoolean.True)
            .map(({ club }) => [club.id, { ...club, admin: false }]),
        ),
      ) || {},
  );
export type SelectClubs = Awaited<ReturnType<typeof getSelectClubsWithSchema>>;
export const useSelectClubs = (
  memberId: number,
  options?: QueryOptions<typeof getSelectClubsWithSchema>,
) =>
  useQueryWrapper(
    ["useSelecstClub", memberId],
    () => getSelectClubsWithSchema(memberId),
    options,
  );

const searchClubsWithSchema = ({
  value,
  offset,
  limit,
}: {
  value: string;
  offset: number;
  limit: number;
}) =>
  getClubs({
    schema: [
      {
        ...selectClubSchema,
        schoolname: 1,
        zipcode: 1,
        specialType: 1,
        filter: `(&(|(|(name=*${value}*)(schoolname=*${value}*))(city=*${value}*))(|(specialType=${SpecialType.Default})(specialType=${SpecialType.Temp})))`,
        sort: [["zipcode", "asc", "abs", 10000]],
        offset,
        limit,
      },
    ],
  });
export const useSearchClubs = (
  props: { value: string; limit: number },
  enabled?: boolean,
) =>
  useInfiniteQuery({
    queryKey: ["useSearchClubs", props.value],
    queryFn: ({ pageParam: offset }) =>
      searchClubsWithSchema({ ...props, offset }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length < props.limit ? undefined : pages.length * props.limit,
    enabled,
  });

export const useSearchClubsLite = (name: string) =>
  useQueryWrapper(
    ["useSearchClubsLite", name],
    () => getClubs({ schema: [{ id: 1, name: 1, filter: `(name=${name})` }] }),
    { enabled: !!name.length },
  );

const schema = createSchema({
  id: 1,
  name: 1,
  street: 1,
  zipcode: 1,
  city: 1,
  schoolclub: 1,
  school: { name: 1 },
  startdate: 1,
  season: {
    startDate: 1,
    endDate: 1,
  },
  specialType: 1,
  active: 1,
  yes2chessreason: 1,
  schack4anreason: 1,
  nameAiGenerated: 1,
  approvalStatus: [
    { referenceid: 1, approved: 1, signed: 1, documentid: 1, updated: 1 },
  ],
  checkvalues: [
    {
      referenceid: 1,
      year: 1,
      approved: 1,
      signed: 1,
      documentid: 1,
      updated: 1,
    },
  ],
  clubStatus: [{ status: 1, year: 1, orderedGift: 1 }],
  files: [{ path: 1, documentType: 1, documentYear: 1, uploadDate: 1 }],
  roles: [
    {
      memberid: 1,
      orgid: 1,
      orgtype: 1,
      orgrole: 1,
      seclevel: 1,
      start: 1,
      end: 1,
      member: {
        id: 1,
        firstname: 1,
        lastname: 1,
        bornyear: 1,
        email: 1,
      },
      filter: `(&(|(orgrole=${RoleType.President})(orgrole=${RoleType.Secretary})(orgrole=${RoleType.BoardMember})(orgrole=${RoleType.ContactPerson})(orgrole=${RoleType.Accountant})(orgrole=${RoleType.Admin}))(end=1970-01-01))`,
    },
  ],
  registrations: [
    {
      membershiptype: 1,
      regstart: 1,
      regend: 1,
      date: 1,
      fee: 1,
      documentid: 1,
      member: {
        id: 1,
        firstname: 1,
        lastname: 1,
        bornyear: 1,
        birthday: 1,
        sex: 1,
        street: 1,
        zipcode: 1,
        city: 1,
        email: 1,
      },
      sort: [["date", "desc"]],
    },
  ],
  district: {
    id: 1,
    name: 1,
    active: 1,
    roles: [
      {
        member: {
          id: 1,
          firstname: 1,
          lastname: 1,
          bornyear: 1,
        },
        orgrole: 1,
        end: 1,
        filter: `(&(orgrole=${RoleType.SuperAuditor})(end=1970-01-01))`,
      },
    ],
  },
  emails: [
    {
      id: 1,
      laststatus: 1,
      laststatustime: 1,
      mailtype: 1,
      senttime: 1,
      clubid: 1,
      memberid: 1,
      recipient: 1,
    },
  ],
});

const getQueryClub = (clubId: number) => getClub(clubId, schema);
export type UseQueryClub = UnSchema<Club, typeof schema>;
export const useQueryClub = (
  clubId?: string | number,
  options?: QueryOptions<typeof getQueryClub>,
) =>
  useQueryWrapper(["useQueryClub", +clubId!], () => getQueryClub(+clubId!), {
    ...options,
    enabled: options?.enabled && !!clubId && !Number.isNaN(+clubId),
  });
export const invalidateClubQuery = (clubId: string | number) =>
  queryClient.invalidateQueries({ queryKey: ["useQueryClub", +clubId] });
export const queryClub = (clubId: string | number) =>
  queryClient.fetchQuery({
    queryKey: ["useQueryClub", +clubId],
    queryFn: () => getClub(+clubId, schema),
  });

export const useCreateMembership = () =>
  useMutationWithAuthError(["UseCreateMembership"], createMembership, {
    onSettled(_result, _error, { clubId }) {
      queryClient.invalidateQueries({ queryKey: ["useQueryClub", clubId] });
    },
  });

export const useReplaceMembership = (
  options?: MutationOptions<typeof replaceMembership>,
) =>
  useMutationWrapper(["useReplaceMembership"], replaceMembership, {
    ...options,
    onSettled(...args) {
      // @ts-ignore
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({
        queryKey: ["useQueryClub", args[2].clubId],
      });
    },
  });

const replaceMembership2 = ({
  oldReg,
  reg,
}: {
  oldReg: Pick<Registration, (typeof regKeys)[number]>;
  reg: Partial<Pick<Registration, (typeof regKeys)[number]>>;
}) => {
  const oldreg = Object.fromEntries(
    regKeys.map((key) => [key, oldReg[key]]),
  ) as Pick<Registration, (typeof regKeys)[number]>;
  const newReg = Object.fromEntries(
    regKeys.map((key) => [key, key in reg ? reg[key] : oldReg[key]]),
  ) as Pick<Registration, (typeof regKeys)[number]>;

  return replaceMembership({
    clubId: newReg.clubid,
    memberId: newReg.memberid,
    oldreg,
    reg: newReg,
  });
};
export const useReplaceMembership2 = (
  options?: MutationOptions<typeof replaceMembership2>,
) => useMutationWrapper(["useReplaceMembership2"], replaceMembership2, options);

export const useMember = (memberId: string | number) =>
  useQueryWrapper(["useMember", Number(memberId)], () =>
    getMember(Number(memberId), {
      firstname: 1,
      lastname: 1,
      birthday: 1,
      sex: 1,
      street: 1,
      zipcode: 1,
      city: 1,
      email: 1,
      phone: 1,
      registrations: [
        {
          clubid: 1,
          date: 1,
          documentid: 1,
          membershiptype: 1,
          regstart: 1,
          regend: 1,
          club: { name: 1 },
        },
      ],
    }),
  );

export const useUploadDocuments = (
  options?: MutationOptions<typeof uploadDocuments>,
) =>
  useMutationWithAuthError(["useUploadDocuments"], uploadDocuments, {
    ...options,
    onSettled(...args) {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({
        queryKey: ["useQueryClub", args[2].clubId],
      });
    },
  });

export const useClubComments = (clubId: number) =>
  useQueryWithError(["useClubComments", clubId], () =>
    getClub(clubId, {
      id: 1,
      name: 1,
      comments: [{ id: 1, comment: 1, timestamp: 1, member: { name: 1 } }],
    }),
  );

export const useAddClubComment = (
  options?: MutationOptions<typeof addComment>,
) =>
  useMutationWithAuthError(["useAddClubComment"], addComment, {
    ...options,
    onSuccess(...args) {
      queryClient.invalidateQueries({
        queryKey: ["useClubComments", args[1].clubId],
      });
      options?.onSuccess?.(...args);
    },
  });

export const useCreateMember = (
  addRole: (memberid: number, clubid: number) => Promise<void>,
  options?: MutationOptions<typeof createMember>,
) =>
  useMutationWithAuthError(
    ["useCreateMember"],
    async (params: Parameters<typeof createMember>[0]) => {
      const response = await createMember(params);
      await addRole(response.result, params.clubid);
      return response;
    },
    options,
  );

export const useApproveClub = (options?: MutationOptions<typeof approveClub>) =>
  useMutationWithAuthError(["useApproveClub"], approveClub, {
    ...options,
    onSettled(_data, _error, { clubId }) {
      queryClient.invalidateQueries({ queryKey: ["useQueryClub", clubId] });
    },
  });

const regKeys = [
  "clubid",
  "memberid",
  "date",
  "regstart",
  "regend",
  "membershiptype",
  "documentid",
] as const;
const approveSignature = (
  data: ClubCheck | Pick<Registration, (typeof regKeys)[number]>,
) => {
  if ("membershiptype" in data) {
    const oldreg = Object.fromEntries(
      regKeys.map((key) => [key, data[key]]),
    ) as Pick<Registration, (typeof regKeys)[number]>;
    return replaceMembership({
      clubId: oldreg.clubid,
      memberId: oldreg.memberid,
      oldreg,
      reg: { ...oldreg, membershiptype: MembershipType.NormalType },
    });
  }

  return updateClubCheckValue({ ...data, approved: true });
};
export const useApproveSignature = (
  options?: MutationOptions<typeof approveSignature>,
) =>
  useMutationWithAuthError(["useApproveSignature"], approveSignature, options);

const unapproveSignature = (
  data: Pick<Registration, (typeof regKeys)[number]>,
) => {
  const oldreg = Object.fromEntries(
    regKeys.map((key) => [key, data[key]]),
  ) as Pick<Registration, (typeof regKeys)[number]>;
  return replaceMembership({
    clubId: oldreg.clubid,
    memberId: oldreg.memberid,
    oldreg,
    reg: { ...oldreg, documentid: undefined },
  });
};
export const useUnapproveSignature = (
  options?: MutationOptions<typeof unapproveSignature>,
) =>
  useMutationWithAuthError(
    ["useUnapproveSignature"],
    unapproveSignature,
    options,
  );

export const teamContendersQuery = (clubId: number) =>
  queryOptions({
    queryKey: ["teamContendersQuery", clubId],
    queryFn: () =>
      getClubTeams({
        clubId,
        schema: [
          {
            id: 1,
            participations: [
              {
                group: {
                  id: 1,
                  name: 1,
                  class: { tournament: { id: 1, name: 1, type: 1, end: 1 } },
                  schack4anteampointsystem: 1,
                },
                result: { place: 1 },
              },
            ],
          },
        ],
      }),
  });

export const memberTournamentsQuery = (memberIds: number[]) =>
  queryOptions({
    queryKey: ["memberTournamentsQuery", memberIds],
    queryFn: () => getMemberTournaments(memberIds),
    enabled: memberIds.length > 0,
  });
